import React, { useState } from 'react';

import { Target, TrendingUp, Award, Clock, Phone, Calendar, AlertTriangle, ArrowUpRight, Star, FileText, User, MessageSquare, DollarSign } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import AddDeadlineModal from '../Modals/AddDeadlineModal';
import CreateProposalModal from '../Modals/CreateProposalModal';
import CreateReportModal from '../Modals/CreateReportModal';
import SalesLeads from '../Sales/SalesLeads';
import { useLeads, useDashboardStats } from '../../hooks/useSupabase';
import AddLeadModal from '../Modals/AddLeadModal';
import { useDeadlines } from '../../hooks/useSupabase';

const SalesPages: React.FC = () => {
  const { user } = useAuth();
  const { addLead } = useLeads();
  const { stats, loading: statsLoading, fetchStats } = useDashboardStats(user?.id, user?.role);
  const { deadlines, fetchDeadlines } = useDeadlines();
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showCreateProposalModal, setShowCreateProposalModal] = useState(false);
  const [showCreateReportModal, setShowCreateReportModal] = useState(false);
  const [showAddDeadlineModal, setShowAddDeadlineModal] = useState(false);
  const [activeView, setActiveView] = useState<'dashboard' | 'leads'>('dashboard');

  // Fetch stats when component mounts
  React.useEffect(() => {
    fetchStats();
    fetchDeadlines();
  }, [fetchStats, fetchDeadlines]);




  // Handler functions for modals
  const handleSaveLead = async (leadData: any): Promise<{ ok: boolean; error?: string }> => {
    try {
      const { data, error } = await addLead(leadData);

      if (error) {
        console.error('Lead ekleme hatası:', error);
        return { ok: false, error: error.message || 'Bilinmeyen hata' };
      }

      // Refresh the leads list
      await fetchStats();

      // Dispatch event for pipeline and dashboard updates
      window.dispatchEvent(new CustomEvent('newLeadAdded', {
        detail: {
          lead: data,
          message: `Yeni lead eklendi: ${leadData.first_name} ${leadData.last_name}`
        }
      }));

      return { ok: true };
    } catch (error) {
      console.error('Lead ekleme hatası:', error);
      return { ok: false, error: 'Beklenmeyen bir hata oluştu' };
    }
  };


  const handleSaveProposal = (proposalData: any) => {
    console.log('Yeni teklif oluşturuldu:', proposalData);
    alert(`${proposalData.studentName} için teklif başarıyla oluşturuldu! Teklifler sayfasından takip edebilirsiniz.`);
    setShowCreateProposalModal(false);
  };

  const handleSaveReport = (reportData: any) => {
    console.log('Yeni rapor oluşturuldu:', reportData);
    setShowCreateReportModal(false);
  };

  const handleSaveDeadline = (deadlineData: any) => {
    console.log('Yeni deadline oluşturuldu:', deadlineData);
    setShowAddDeadlineModal(false);
    alert(`Deadline başarıyla oluşturuldu! ${deadlineData.affectedUsers.length} kullanıcıya bildirim gönderildi.`);
    fetchDeadlines(); // Refresh deadlines
  };

  const followUpLeads = stats.todayFollowUps;

  // Yaklaşan deadline'lar (kullanıcıya özel)
  const userDeadlines = deadlines.filter(deadline =>
    deadline.affectedUsers?.includes(user?.id || '') && deadline.isActive
  );

  const urgentDeadlines = userDeadlines.filter(deadline => {
    const now = new Date();
    const deadlineDate = new Date(deadline.dueDate);
    const diffTime = deadlineDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays >= 0;
  }).sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());

  const getFollowUpStatus = (followUpDate: string) => {
    const date = new Date(followUpDate);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { status: 'overdue', color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400', text: 'Gecikmiş' };
    if (diffDays === 0) return { status: 'today', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400', text: 'Bugün' };
    if (diffDays === 1) return { status: 'tomorrow', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400', text: 'Yarın' };
    if (diffDays <= 3) return { status: 'soon', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400', text: `${diffDays} gün` };
    return { status: 'upcoming', color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400', text: `${diffDays} gün` };
  };

  const getDeadlineStatus = (dueDate: Date) => {
    const now = new Date();
    const diffTime = dueDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { status: 'overdue', color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400', text: 'Gecikmiş' };
    if (diffDays === 0) return { status: 'today', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400', text: 'Bugün' };
    if (diffDays <= 3) return { status: 'urgent', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400', text: `${diffDays} gün` };
    return { status: 'upcoming', color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400', text: `${diffDays} gün` };
  };

  const handleLeadsNavigation = () => {
    setActiveView('leads');
  };

  const handleDashboardNavigation = () => {
    setActiveView('dashboard');
  };

  // If viewing leads, show the SalesLeads component
  if (activeView === 'leads') {
    return (
      <div className="space-y-6">
        {/* Navigation */}
        <div className="flex items-center space-x-4">
          <button
            onClick={handleDashboardNavigation}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300"
          >
            <span>← Dashboard'a Dön</span>
          </button>
        </div>

        <SalesLeads />
      </div>
    );
  }





  return (
    <div className="space-y-6">
      {/* Hızlı İşlemler */}
      <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border border-gray-100 dark:border-gray-800 rounded-3xl p-6 shadow-xl shadow-gray-200/50 dark:shadow-none">
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
          Hızlı İşlemler
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => setShowAddLeadModal(true)}
            className="flex items-center space-x-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50/50 hover:from-blue-100 hover:to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30 border border-blue-100 dark:border-blue-800/50 rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg shadow-sm group"
          >
            <div className="bg-blue-500/10 p-3 rounded-xl group-hover:bg-blue-500/20 transition-colors">
              <Target className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="text-left">
              <p className="font-bold text-gray-800 dark:text-gray-100">Yeni Lead Ekle</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Potansiyel müşteri</p>
            </div>
          </button>
          <button
            onClick={() => setShowCreateProposalModal(true)}
            className="flex items-center space-x-4 p-4 bg-gradient-to-br from-teal-50 to-emerald-50/50 hover:from-teal-100 hover:to-emerald-100 dark:from-teal-900/20 dark:to-emerald-900/20 dark:hover:from-teal-900/30 dark:hover:to-emerald-900/30 border border-teal-100 dark:border-teal-800/50 rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg shadow-sm group"
          >
            <div className="bg-teal-500/10 p-3 rounded-xl group-hover:bg-teal-500/20 transition-colors">
              <FileText className="w-6 h-6 text-teal-600 dark:text-teal-400" />
            </div>
            <div className="text-left">
              <p className="font-bold text-gray-800 dark:text-gray-100">Teklif Hazırla</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Müşteri teklifi</p>
            </div>
          </button>
          <button
            onClick={() => setShowAddDeadlineModal(true)}
            className="flex items-center space-x-4 p-4 bg-gradient-to-br from-red-50 to-rose-50/50 hover:from-red-100 hover:to-rose-100 dark:from-red-900/20 dark:to-rose-900/20 dark:hover:from-red-900/30 dark:hover:to-rose-900/30 border border-red-100 dark:border-red-800/50 rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg shadow-sm group"
          >
            <div className="bg-red-500/10 p-3 rounded-xl group-hover:bg-red-500/20 transition-colors">
              <Clock className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="text-left">
              <p className="font-bold text-gray-800 dark:text-gray-100">Deadline Ekle</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Ekip hatırlatıcısı</p>
            </div>
          </button>
        </div>
      </div>





      {/* Ana İçerik Alanları */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Kişisel Satış Raporu (Bu Ay) */}
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border border-gray-100 dark:border-gray-800 rounded-3xl p-8 shadow-xl shadow-gray-200/50 dark:shadow-none">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-500" />
              Kişisel Satış Raporu
              <span className="text-sm font-normal text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                {new Date().toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
              </span>
            </h3>
            <button
              onClick={handleLeadsNavigation}
              className="text-indigo-600 hover:text-indigo-700 text-sm font-medium hover:underline flex items-center gap-1"
            >
              Tüm Kayıtlar <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Bu Ay Yeni Eklenen */}
            <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl flex items-center justify-center">
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Eklenen Lead (Bu Ay)</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {stats.addedMonth}
                    <span className="text-xs font-normal text-gray-400 ml-1">kişi</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Bu Ay Yapılan Satış */}
            <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-xl flex items-center justify-center">
                  <Award className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Kayıt Olan (Bu Ay)</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {stats.enrolledMonth}
                    <span className="text-xs font-normal text-gray-400 ml-1">kişi</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Ortalama Kayıt Tutarı */}
            <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Kayıt Tutarı</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(stats.revenue || 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Performans Özeti - Artık burada */}
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border border-gray-100 dark:border-gray-800 rounded-3xl p-8 shadow-xl shadow-gray-200/50 dark:shadow-none">
          <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-6">Performans Özeti</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-5 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-100/50 dark:border-blue-800/20">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-gray-700 dark:text-gray-200 font-semibold">Dönüşüm Oranı</span>
              </div>
              <span className="text-2xl font-black text-blue-600 dark:text-blue-400">
                %{stats.conversionRate.toFixed(1)}
              </span>
            </div>
            <div className="flex items-center justify-between p-5 bg-gradient-to-r from-amber-50/80 to-orange-50/80 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl border border-amber-100/50 dark:border-amber-800/20">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Calendar className="w-5 h-5 text-amber-600" />
                </div>
                <span className="text-gray-700 dark:text-gray-200 font-semibold">Bu Ay Eklenen</span>
              </div>
              <span className="text-2xl font-black text-amber-600 dark:text-amber-400">
                {stats.addedMonth}
              </span>
            </div>
            <div className="flex items-center justify-between p-5 bg-gradient-to-r from-emerald-50/80 to-teal-50/80 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-2xl border border-emerald-100/50 dark:border-emerald-800/20">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Clock className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="text-gray-700 dark:text-gray-200 font-semibold">Bu Hafta Eklenen</span>
              </div>
              <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
                {stats.addedWeek}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Detaylı Hatırlatıcılar Bölümü */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Follow-up Hatırlatıcıları */}
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border border-gray-100 dark:border-gray-800 rounded-3xl p-8 shadow-xl shadow-gray-200/50 dark:shadow-none">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <span className="w-2 h-8 bg-blue-500 rounded-full inline-block"></span>
              Follow-up'lar <span className="text-gray-400 font-normal">({followUpLeads.length})</span>
            </h3>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('changeTab', { detail: 'follow-ups' }))}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium hover:underline"
            >
              Tümünü Gör →
            </button>
          </div>

          {followUpLeads.length === 0 ? (
            <div className="text-center py-10 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
              <Target className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 text-sm">Follow-up gerektiren lead yok</p>
            </div>
          ) : (
            <div className="space-y-4">
              {followUpLeads.slice(0, 5).map((lead: any) => {
                const followUpInfo = getFollowUpStatus(lead.follow_up_date);
                return (
                  <div key={lead.id} className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl hover:shadow-md transition-all duration-300 transform hover:-translate-x-1 cursor-pointer group">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                        <span className="text-white text-xs font-bold">
                          {(lead.first_name?.[0] || '') + (lead.last_name?.[0] || '')}
                        </span>
                      </div>
                      <div>
                        <p className="font-bold text-gray-800 dark:text-white text-sm group-hover:text-blue-600 transition-colors">
                          {lead.first_name} {lead.last_name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 flex items-center gap-1 mt-0.5">
                          <Calendar className="w-3 h-3" />
                          {format(new Date(lead.follow_up_date), 'dd MMM', { locale: tr })}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${followUpInfo.color}`}>
                      {followUpInfo.text}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Deadline'lar */}
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border border-gray-100 dark:border-gray-800 rounded-3xl p-8 shadow-xl shadow-gray-200/50 dark:shadow-none">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <span className="w-2 h-8 bg-red-500 rounded-full inline-block"></span>
              Deadline'lar <span className="text-gray-400 font-normal">({urgentDeadlines.length})</span>
            </h3>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('changeTab', { detail: 'deadlines' }))}
              className="text-red-600 hover:text-red-700 text-sm font-medium hover:underline"
            >
              Tümünü Gör →
            </button>
          </div>

          {urgentDeadlines.length === 0 ? (
            <div className="text-center py-10 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
              <AlertTriangle className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 text-sm">Yaklaşan deadline yok</p>
              <button
                onClick={() => setShowAddDeadlineModal(true)}
                className="mt-3 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-sm font-medium transition-colors"
              >
                Deadline Ekle
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {urgentDeadlines.slice(0, 5).map((deadline) => {
                const deadlineInfo = getDeadlineStatus(deadline.dueDate);
                return (
                  <div key={deadline.id} className="p-5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl hover:shadow-md transition-all duration-300 transform hover:-translate-x-1 cursor-pointer group">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="font-bold text-gray-800 dark:text-white text-sm group-hover:text-red-600 transition-colors">
                        {deadline.title}
                      </h5>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${deadlineInfo.color}`}>
                        {deadlineInfo.text}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {format(deadline.dueDate, 'dd MMM yyyy', { locale: tr })}
                      </p>
                      <div className="flex items-center space-x-1.5">
                        <div className={`w-2.5 h-2.5 rounded-full ${deadline.priority === 'critical' ? 'bg-red-500 shadow-red-500/50 shadow-sm' :
                          deadline.priority === 'high' ? 'bg-orange-500' :
                            deadline.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                          }`} />
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-500">
                          {deadline.priority === 'critical' ? 'Kritik' :
                            deadline.priority === 'high' ? 'Yüksek' :
                              deadline.priority === 'medium' ? 'Orta' : 'Düşük'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={showAddLeadModal}
        onClose={() => setShowAddLeadModal(false)}
        onSave={handleSaveLead}
      />

      {/* Create Proposal Modal */}
      <CreateProposalModal
        isOpen={showCreateProposalModal}
        onClose={() => setShowCreateProposalModal(false)}
        onSave={handleSaveProposal}
      />

      {/* Create Report Modal */}
      <CreateReportModal
        isOpen={showCreateReportModal}
        onClose={() => setShowCreateReportModal(false)}
        onSave={handleSaveReport}
      />

      {/* Add Deadline Modal */}
      <AddDeadlineModal
        isOpen={showAddDeadlineModal}
        onClose={() => setShowAddDeadlineModal(false)}
        onSave={handleSaveDeadline}
      />
    </div>
  );
};

export default SalesPages;